import { API_CONFIG, TIMEOUTS } from '../constants/config';
import { BackendUser } from './auth';
import axios from 'axios';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

export interface SubscriptionStatus {
  subscription_plan: string;
  subscription_status: string;
  subscription_expires_at?: string;
  trial_started_at?: string;
  trial_expires_at?: string;
  is_premium: boolean;
  is_pro: boolean;
  is_plus: boolean;
  is_on_trial: boolean;
}

export interface PlanLimits {
  analyses_per_month: number | null;
  wardrobe_item_limit: number | null;
  moodboard_limit: number | null;
  save_export: boolean;
  analysis_history: boolean;
  advanced_ai: boolean;
  early_access: boolean;
  pdf_reports: boolean;
  priority_support: boolean;
  full_virtual_drape: boolean;
  /** Pricing (from backend, matches UI). */
  price_display?: string;
  period?: string;
  monthly_cents?: number;
  annual_cents?: number;
}

export interface PlansResponse {
  plans: Record<string, PlanLimits>;
  valid_plans: string[];
}

export interface CheckoutResponse {
  checkout_url: string;
  transaction_id?: string;
}

export interface TrialExpirationCheck {
  trial_expired: boolean;
  message?: string;
  subscription_plan: string;
  trial_expires_at?: string;
}

/** Usage and remaining credits for dashboard. */
export interface SubscriptionUsage {
  analyses_used: number;
  analyses_limit: number | null;
  analyses_remaining: number | null;
  wardrobe_used: number;
  wardrobe_limit: number | null;
  wardrobe_remaining: number | null;
}

// Create axios instance with configuration
const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

export const subscriptionAPI = {
  /**
   * Get subscription status
   */
  getStatus: async (): Promise<SubscriptionStatus> => {
    const response = await api.get<SubscriptionStatus>('/subscriptions/status');
    return response.data;
  },

  /**
   * Get usage and remaining credits (analyses this month, wardrobe items).
   */
  getUsage: async (): Promise<SubscriptionUsage> => {
    const response = await api.get<SubscriptionUsage>('/subscriptions/usage');
    return response.data;
  },

  /**
   * Get plan limits and features (public)
   */
  getPlans: async (): Promise<PlansResponse> => {
    const response = await api.get<PlansResponse>('/subscriptions/plans');
    return response.data;
  },

  /**
   * Create payment checkout for Plus or Pro. Returns checkout_url for redirect.
   */
  checkout: async (params: {
    plan_id: 'plus' | 'pro';
    interval?: 'monthly' | 'annual';
    success_url?: string;
    cancel_url?: string;
  }): Promise<CheckoutResponse> => {
    const response = await api.post<CheckoutResponse>(
      '/subscriptions/checkout',
      {
        plan_id: params.plan_id,
        interval: params.interval ?? 'monthly',
        success_url: params.success_url ?? '',
        cancel_url: params.cancel_url ?? '',
      }
    );
    return response.data;
  },

  /**
   * Upgrade to Plus subscription (stub when payment provider not configured)
   */
  upgradeToPlus: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>(
      '/subscriptions/upgrade-to-plus'
    );
    return response.data;
  },

  /**
   * Upgrade to Pro subscription
   */
  upgradeToPro: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>(
      '/subscriptions/upgrade-to-pro'
    );
    return response.data;
  },

  /**
   * Start 7-day freemium trial
   */
  startFreemiumTrial: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>('/subscriptions/start-trial');
    return response.data;
  },

  /**
   * Downgrade to free subscription
   */
  downgradeToFree: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>(
      '/subscriptions/downgrade-to-free'
    );
    return response.data;
  },

  /**
   * Purchase pay-per-scan analysis
   */
  purchasePayPerScan: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>('/subscriptions/pay-per-scan');
    return response.data;
  },

  /**
   * Check trial expiration status
   */
  checkTrialExpiration: async (): Promise<TrialExpirationCheck> => {
    const response = await api.post<TrialExpirationCheck>(
      '/subscriptions/check-trial-expiration'
    );
    return response.data;
  },

  /**
   * Restore subscription
   */
  restore: async (): Promise<BackendUser> => {
    const response = await api.post<BackendUser>('/subscriptions/restore');
    return response.data;
  },

  // Legacy methods for backward compatibility
  /**
   * Legacy upgrade method
   */
  upgrade: async (): Promise<BackendUser> => {
    return await subscriptionAPI.upgradeToPro();
  },

  /**
   * Legacy downgrade method
   */
  downgrade: async (): Promise<BackendUser> => {
    return await subscriptionAPI.downgradeToFree();
  },

  /**
   * Cancel subscription
   */
  cancel: async (): Promise<BackendUser> => {
    return await subscriptionAPI.downgradeToFree();
  },
};
